import React, { useState, useEffect } from 'react';
import { usePricing } from '../../contexts/PricingContext';
import { FaSave, FaCheck, FaTimes } from 'react-icons/fa';
import './PricingSettings.css';

/**
 * PricingSettings component allows users to configure pricing for quotes
 */
const PricingSettings = () => {
  const {
    pricingData,
    presetData,
    isLoading,
    isAdmin,
    updatePrice,
    resetToDefaults,
    savePricingToLocalStorage,
    clearLocalStorage
  } = usePricing();
  
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ visible: false, type: '', message: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentPricingData, setCurrentPricingData] = useState(null);

  // Initialize current pricing data
  useEffect(() => {
    if (pricingData) {
      setCurrentPricingData(JSON.parse(JSON.stringify(pricingData)));
    }
  }, [pricingData]);

  // Handle toast notifications
  useEffect(() => {
    let toastTimer;
    if (toast.visible) {
      toastTimer = setTimeout(() => {
        setToast({ ...toast, visible: false });
      }, 3000);
    }
    return () => clearTimeout(toastTimer);
  }, [toast]);

  // Helper to show toast messages
  const showToast = (type, message) => {
    setToast({
      visible: true,
      type,
      message
    });
  };

  // Handle save changes to localStorage
  const handleSaveChanges = async () => {
    if (!currentPricingData) return;
    
    try {
      setIsSaving(true);
      setErrorMessage('');
      
      const success = savePricingToLocalStorage(currentPricingData);
      
      if (success) {
        showToast('success', 'Pricing settings saved');
        setHasUnsavedChanges(false);
      } else {
        setErrorMessage('Failed to save pricing settings');
      }
    } catch (error) {
      setErrorMessage('Failed to save pricing settings');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle price changes
  const handlePriceChange = (section, item, price) => {
    if (!currentPricingData) return;
    
    // If price is an empty string, store it as is to allow for empty field editing
    // Otherwise convert to number
    const priceValue = price === '' ? '' : Number(price);
    
    // Update local state
    const newPricingData = { ...currentPricingData };
    if (!newPricingData[section]) newPricingData[section] = {};
    if (!newPricingData[section][item]) newPricingData[section][item] = {};
    
    newPricingData[section][item].price = priceValue;
    
    setCurrentPricingData(newPricingData);
    setHasUnsavedChanges(true);
  };

  // Handle save presets
  const handleSavePresets = async () => {
    try {
      setIsSaving(true);
      setErrorMessage('');
      
      // Add code here to save presets when it's implemented
      
      showToast('success', 'Presets saved successfully');
    } catch (error) {
      setErrorMessage('Failed to save presets');
      console.error('Save presets error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle reset to defaults
  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all pricing to default values? This will discard all your changes.')) {
      return;
    }
    
    setIsSaving(true);
    setErrorMessage('');
    
    try {
      await resetToDefaults();
      showToast('success', 'Settings reset to defaults');
      setHasUnsavedChanges(false);
    } catch (error) {
      setErrorMessage('Failed to reset settings');
      console.error('Reset error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle input blur events - default to 0 if empty
  const handleBlur = (e, section, item) => {
    const value = e.target.value;
    if (value === '') {
      handlePriceChange(section, item, '0');
    }
  };
  
  // Handle key down events
  const handleKeyDown = (e, section, item) => {
    // If Escape key is pressed, revert to 0 if field is empty
    if (e.key === 'Escape' && e.target.value === '') {
      handlePriceChange(section, item, '0');
      e.target.blur();
    } else if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Get display name for field item
  const getFieldDisplayName = (item) => {
    const displayNames = {
      entry: "Entry",
      oa: "Open Area",
      waffleMeeting: "Waffle Standard",
      waffleDesk: "Waffle Desk & Phonebooth"
    };
    
    return displayNames[item] || item.charAt(0).toUpperCase() + item.slice(1);
  };

  // Render item field with appropriate formatting
  const renderItemField = (section, item, value) => {
    if (!currentPricingData || !currentPricingData[section]) return null;
    
    // Use the local state value
    const currentValue = currentPricingData[section][item]?.price || 0;
    
    return (
      <div className="price-field" key={`${section}-${item}`}>
        <label>
          {getFieldDisplayName(item)}:
          <div className="input-group">
            <span className="currency">$</span>
            <input
              type="number"
              value={currentValue}
              onChange={(e) => handlePriceChange(section, item, e.target.value)}
              onBlur={(e) => handleBlur(e, section, item)}
              onKeyDown={(e) => handleKeyDown(e, section, item)}
              min="0"
              step="0.01"
              className="price-input"
            />
          </div>
        </label>
      </div>
    );
  };

  // Render a complete pricing section
  const renderSection = (section, title) => {
    if (!currentPricingData || !currentPricingData[section]) return null;
    
    return (
      <div className="price-section">
        <div className="section-header">
          <h3>{title}</h3>
        </div>
        
        <div className="section-content">
          <div className="price-fields">
            {Object.entries(currentPricingData[section]).map(([item, value]) => 
              renderItemField(section, item, value)
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading || !currentPricingData) {
    return (
      <div className="pricing-settings loading">
        <svg className="spinner" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
        </svg>
        Loading pricing settings...
      </div>
    );
  }

  return (
    <div className="pricing-settings">
      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
      
      {toast.visible && (
        <div className={`toast-message ${toast.type}`}>
          {toast.type === 'success' ? <FaCheck /> : <FaTimes />}
          {toast.message}
        </div>
      )}

      <div className="presets-header">
        <h2>Presets</h2>
        <div className="preset-actions">
          <button 
            type="button" 
            className={`save-button ${hasUnsavedChanges ? 'has-changes' : ''}`}
            disabled={isSaving || !hasUnsavedChanges}
            onClick={handleSaveChanges}
          >
            <FaSave /> {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
          
          {isAdmin && (
            <button 
              type="submit" 
              className="save-presets-button" 
              disabled={isSaving} 
              onClick={handleSavePresets}
            >
              {isSaving ? (
                <>
                  <FaSave /> Auto-saving
                </>
              ) : (
                <>
                  <FaSave /> Save Presets
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="pricing-description">
        <p>
          Changes are saved to your browser's storage. Your pricing preferences will persist between sessions on this device.
        </p>
        {hasUnsavedChanges && (
          <div className="unsaved-changes-notice">
            <strong>You have unsaved changes.</strong> Click "Save Changes" to store your updates.
          </div>
        )}
      </div>

      <div className="pricing-content">
        <div className="pricing-columns-header variant-3">
          <div className="column-header one-time">
            <span className="header-label">One-time Costs</span>
          </div>
          <div className="column-header annual">
            <span className="header-label">Annual Costs</span>
          </div>
        </div>
        <div className="pricing-sections-container">
          <div className="pricing-column one-time">
            {renderSection('hardware', 'Hardware')}
            {renderSection('deployment', 'Deployment')}
            {renderSection('installation', 'Estimated Installation')}
          </div>
          <div className="pricing-column annual">
            {renderSection('software', 'Software')}
            {renderSection('support', 'Setup Services')}
          </div>
        </div>
        
        <div className="presets-footer">
          <button 
            className="reset-defaults-button" 
            onClick={handleReset}
            disabled={isSaving}
          >
            {isSaving ? 'Resetting' : 'Reset to Defaults'}
          </button>
          
          <div className="version-info">
            <span className="version-number">v1.0.5</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSettings;